import styled from 'styled-components';

export const Shell = styled.div`
  --order-background: ${(p) => (p.theme.title === 'LIGHT' ? '#DADAE3' : p.theme.colors.background)};
  --order-surface: ${(p) => (p.theme.title === 'LIGHT' ? '#eeeeee' : p.theme.colors.surface)};
  background: var(--order-background);
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
  line-height: 1.5;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  svg {
    color: currentColor;
    flex-shrink: 0;
  }
  button,
  input {
    font: inherit;
  }
  button {
    cursor: pointer;
  }
  button:focus-visible,
  input:focus-visible,
  summary:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 3px;
  }
`;

export const Tabs = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  gap: 4px;
  min-height: 64px;
  padding: 5px 16px;
  overflow-x: auto;
  background: ${(p) => p.theme.colors.primary};
  button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 76px;
    padding: 5px 12px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: ${(p) => p.theme.colors.onPrimary};
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
    transition: background 150ms;
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      background: ${(p) => p.theme.colors.primaryDark};
    }
    &[aria-selected='true'] {
      background: var(--order-surface);
      color: ${(p) => p.theme.colors.text};
    }
    &:focus-visible {
      outline-color: ${(p) => p.theme.colors.onPrimary};
      outline-offset: -3px;
    }
  }
  @media (max-width: 640px) {
    justify-content: flex-start;
    padding: 8px;
    button {
      min-width: 76px;
      padding: 7px 10px;
    }
  }
`;

export const Content = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: max-content;
  align-content: start;
  gap: 16px;
  overflow-y: auto;
  @media (max-width: 640px) {
    padding: 12px;
    gap: 12px;
  }
`;

export const Hero = styled.section`
  overflow: hidden;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.onPrimary};
  .hero-heading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 22px;
    min-width: 0;
  }
  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 800;
  }
  p {
    margin: 4px 0 0;
    opacity: 0.8;
    overflow-wrap: anywhere;
  }
  .hero-icon {
    display: inline-flex;
    padding: 12px;
    border-radius: 14px;
    background: var(--order-surface);
    color: ${(p) => p.theme.colors.primary};
  }
  .hero-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    background: ${(p) => p.theme.colors.primaryDark};
  }
  .hero-meta > div {
    min-width: 0;
    padding: 12px 20px;
  }
  .hero-meta span {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    opacity: 0.75;
    letter-spacing: 0.06em;
  }
  .hero-meta strong {
    display: block;
    margin-top: 4px;
    overflow-wrap: anywhere;
    font-size: 12px;
  }
  @media (max-width: 640px) {
    .hero-heading {
      padding: 16px;
    }
    h2 {
      font-size: 19px;
    }
  }
`;

export const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;

export const Card = styled.section`
  min-width: 0;
  background: var(--order-surface);
  border: 3px solid var(--order-background);
  border-radius: 16px;
  padding: 20px;
  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 800;
    margin-bottom: 16px;
  }
  h3 > svg {
    box-sizing: content-box;
    padding: 9px;
    border-radius: 12px;
    background: var(--order-background);
    color: ${(p) => p.theme.colors.primary};
  }
  dl {
    margin: 0;
  }
  @media (max-width: 640px) {
    padding: 16px;
  }
`;

export const Field = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 16px;
  align-items: baseline;
  padding: 10px 0;
  border-bottom: 1px solid var(--order-background);
  &:last-child {
    border-bottom: 0;
  }
  dt {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textLight};
  }
  dd {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-align: right;
    overflow-wrap: anywhere;
  }
`;

export const Badge = styled.span<{ $tone?: 'positive' | 'negative' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  background: var(--order-surface);
  color: ${(p) => (p.$tone ? p.theme.colors[p.$tone] : p.theme.colors.textLight)};
`;

export const Hint = styled.p`
  margin: 0 0 16px;
  color: ${(p) => p.theme.colors.textLight};
  font-size: 12px;
`;

export const Note = styled.div`
  padding: 14px;
  margin-top: 10px;
  border-radius: 12px;
  background: var(--order-background);
  strong {
    display: block;
    font-size: 11px;
    margin-bottom: 6px;
    color: ${(p) => p.theme.colors.textLight};
  }
  p {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

export const Search = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--order-background);
  padding: 12px 14px;
  border-radius: 12px;
  margin-bottom: 16px;
  color: ${(p) => p.theme.colors.textLight};
  input {
    min-width: 0;
    width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: ${(p) => p.theme.colors.text};
  }
`;

export const CustomerName = styled.p`
  padding: 12px;
  border-radius: 12px;
  background: var(--order-background);
  font-size: 16px;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

export const Metrics = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.75fr) repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin: 12px 0 16px;
  > div {
    min-width: 0;
    padding: 6px 4px;
    border: 1px solid var(--order-background);
    border-radius: 8px;
    background: var(--order-background);
    text-align: center;
  }
  dt {
    color: ${(p) => p.theme.colors.textLight};
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  dd {
    margin: 3px 0 0;
    font-size: 14px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: 640px) {
    dt {
      font-size: 8px;
    }
    dd {
      font-size: 12px;
    }
  }
`;

export const FinancialPanel = styled.div`
  background: var(--order-background);
  border-radius: 12px;
  padding: 16px;
  .financial-line,
  .financial-total {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 0;
  }
  .financial-line > div {
    text-align: right;
  }
  .financial-line strong,
  .financial-total strong {
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  .financial-line small {
    display: block;
    font-size: 11px;
    color: ${(p) => p.theme.colors.textLight};
    margin-top: 3px;
  }
  .compact {
    font-size: 12px;
  }
  .discount {
    color: ${(p) => p.theme.colors.negative};
  }
  .surcharge {
    color: ${(p) => p.theme.colors.positive};
  }
  .financial-total {
    margin-top: 10px;
    padding-top: 16px;
    border-top: 1px solid var(--order-background);
    align-items: baseline;
    font-weight: 800;
  }
  .financial-total strong {
    font-size: 26px;
    color: ${(p) => p.theme.colors.primary};
    text-align: right;
  }
  @media (max-width: 520px) {
    padding: 12px;
    .financial-total strong {
      font-size: 21px;
    }
    .financial-total {
      flex-wrap: wrap;
    }
  }
`;

export const PriceTable = styled.section`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.onPrimary};
  .price-icon {
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: 12px;
    border-radius: 12px;
    background: var(--order-surface);
    color: ${(p) => p.theme.colors.primary};
  }
  > div {
    min-width: 0;
  }
  div > span {
    font-size: 10px;
    text-transform: uppercase;
    opacity: 0.75;
  }
  strong,
  small {
    display: block;
    overflow-wrap: anywhere;
  }
  strong {
    margin: 3px 0;
    font-size: 15px;
  }
  small {
    opacity: 0.8;
  }
`;

export const GeneralDiscount = styled.section`
  display: flex;
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--order-background);
  border-radius: 12px;
  background: var(--order-surface);
  label {
    display: block;
    width: 144px;
    font-size: 10px;
    color: ${(p) => p.theme.colors.textLight};
  }
  input {
    display: block;
    width: 100%;
    border: 0;
    border-radius: 10px;
    padding: 10px;
    background: var(--order-background);
    color: ${(p) => p.theme.colors.text};
  }
  @media (max-width: 640px) {
    label {
      width: 100%;
    }
  }
`;

export const ItemsPanel = styled.section`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  min-width: 0;
  padding: 24px;
  border-radius: 28px;
  background: var(--order-surface);
  > h2 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 20px;
  }
  > dl {
    margin: 12px 0 16px;
  }
  @media (max-width: 640px) {
    padding: 16px;
    border-radius: 16px;
  }
`;

export const ItemCount = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${(p) => p.theme.colors.textLight};
  margin-bottom: 12px;
`;

const itemPresentation = `
  .product-name { display: block; font-size: 14px; overflow-wrap: anywhere; }
  .product-code { display: block; margin-top: 3px; font-size: 12px; }
  .adjustments { display: flex; flex-direction: column; gap: 3px; margin-top: 7px; font-size: 11px; font-weight: 600; }
  .item-extra { margin-top: 12px; }
  summary { cursor: pointer; font-size: 12px; }
  .item-detail { margin-top: 12px; }
`;

export const ItemList = styled.div`
  display: grid;
  gap: 12px;
`;

export const ItemCard = styled.article`
  ${itemPresentation}
  min-width: 0;
  border: 3px solid ${(p) => p.theme.colors.positive};
  border-radius: 16px;
  padding: 16px;
  .item-heading {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
  }
  .product-icon {
    width: 80px;
    height: 80px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 16px;
    background: var(--order-background);
    color: ${(p) => p.theme.colors.textLight};
  }
  .item-main {
    min-width: 0;
  }
  .product-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .product-code,
  .item-equation span {
    color: ${(p) => p.theme.colors.textLight};
  }
  .item-equation {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    margin-top: 10px;
  }
  .item-equation strong {
    font-size: 16px;
  }
  .item-equation span {
    font-size: 12px;
  }
  .item-equation .discount {
    color: ${(p) => p.theme.colors.negative};
  }
  .item-equation .surcharge {
    color: ${(p) => p.theme.colors.positive};
  }
  summary {
    color: ${(p) => p.theme.colors.primary};
  }
  .item-extra {
    margin: 0;
    align-self: center;
  }
  .item-extra > summary {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid ${(p) => p.theme.colors.primary};
    border-radius: 10px;
    list-style: none;
  }
  .item-extra[open] {
    grid-column: 1 / -1;
    width: 100%;
  }
  @media (max-width: 640px) {
    padding: 16px;
    .item-heading {
      grid-template-columns: auto minmax(0, 1fr);
    }
    .item-extra {
      grid-column: 1 / -1;
      justify-self: end;
    }
  }
`;

export const SummaryTable = styled.div`
  ${itemPresentation}
  .unit-prices {
    display: inline-flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: baseline;
    gap: 6px;
  }
  border: 3px solid var(--order-background);
  border-radius: 12px;
  overflow: hidden;
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  caption {
    padding: 10px 12px;
    text-align: left;
    color: ${(p) => p.theme.colors.textLight};
    font-size: 12px;
  }
  th {
    color: ${(p) => p.theme.colors.textLight};
    font-size: 10px;
    text-transform: uppercase;
    background: var(--order-background);
  }
  th,
  td {
    padding: 12px;
    vertical-align: top;
    text-align: right;
    overflow-wrap: anywhere;
  }
  th:first-child,
  td:first-child {
    text-align: left;
    width: 43%;
  }
  tr + tr {
    border-top: 3px solid var(--order-background);
  }
  del {
    display: inline;
    margin-right: 6px;
    font-size: 11px;
    color: ${(p) => p.theme.colors.textLight};
  }
  .product-code {
    color: ${(p) => p.theme.colors.textLight};
  }
  .discount {
    color: ${(p) => p.theme.colors.negative};
  }
  .surcharge {
    color: ${(p) => p.theme.colors.positive};
  }
  summary {
    color: ${(p) => p.theme.colors.primary};
  }
  @media (max-width: 900px) {
    table,
    tbody {
      display: block;
    }
    thead {
      display: none;
    }
    tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
    }
    td {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
      padding: 6px 12px;
      min-width: 0;
      font-size: 14px;
    }
    td:first-child {
      display: block;
      grid-column: 1 / -1;
      padding: 12px;
      width: auto;
    }
    td[data-label]:last-child {
      padding-bottom: 12px;
    }
    td[data-label]::before {
      content: attr(data-label);
      display: block;
      margin-bottom: 0;
      color: ${(p) => p.theme.colors.textLight};
      font-size: 9px;
      text-transform: uppercase;
    }
  }
`;
